import { useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BRAND_NAME } from '@shared/brand';
import { idr } from '@shared/format';
import { CodeBlock, Icon, Tabs } from '../../components/ui';
import { Callout, DocSection, DocTable } from '../../components/DocBlocks';
import { API_KEY_ENV, API_ORIGIN, BASE_URL } from '../../snippets';
import { AppLink, DocLink, PageHead, pretty, round2, useDocs } from './context';
import { PAGES, docPath } from './registry';

/* Biaya dan batas */

const money = (value: number) => idr(round2(value), true);

export function BillingPage() {
  return (
    <>
      <PageHead slug="biaya">
        Anda hanya membayar yang benar-benar terpakai. Supaya saldo tidak minus, biaya terbesar ditahan dulu saat request masuk.
      </PageHead>

      <DocSection id="saldo" title="Cara saldo dipotong">
        <p>Contoh satu request chat, dari masuk sampai selesai:</p>
        <ol className="docs-ledger">
          <li className="docs-ledger-row">
            <span className="docs-ledger-op hold">
              <span className="dot" />
              Ditahan
            </span>
            <span className="docs-ledger-what">
              Saat request masuk: perkiraan token prompt dan <code>max_tokens</code> dikali harga, ditambah cadangan 15%.
              <small>1.200 token masuk × Rp40.000/1&nbsp;jt + 1.000 token keluar × Rp200.000/1&nbsp;jt, ×&nbsp;1,15</small>
            </span>
            <span className="docs-ledger-amt">{money(285.2)}</span>
          </li>
          <li className="docs-ledger-row">
            <span className="docs-ledger-op charge">
              <span className="dot" />
              Ditagih
            </span>
            <span className="docs-ledger-what">
              Setelah selesai: hanya token yang benar-benar dipakai.
              <small>1.200 token masuk + 380 token keluar</small>
            </span>
            <span className="docs-ledger-amt">{money(124)}</span>
          </li>
          <li className="docs-ledger-row">
            <span className="docs-ledger-op release">
              <span className="dot" />
              Dikembalikan
            </span>
            <span className="docs-ledger-what">Sisa tahanan langsung kembali ke saldo tersedia.</span>
            <span className="docs-ledger-amt">{money(161.2)}</span>
          </li>
        </ol>
        <p className="docs-caption">Harga di atas hanya contoh.</p>
        <ul>
          <li>
            Bila saldo tersedia kurang dari jumlah yang perlu ditahan, request ditolak dengan 402 <code>insufficient_balance</code>.
            Tidak ada biaya dan provider tidak dipanggil.
          </li>
          <li>
            Harga tercantum dalam Rupiah per 1 juta token, masukan dan keluaran terpisah. Token masukan yang ter-cache lebih murah.
          </li>
          <li>
            <code>max_tokens</code> yang besar tidak membuat request lebih mahal. Hanya saldo yang ditahan selama request berjalan
            jadi lebih banyak.
          </li>
          <li>
            Rincian per request ada di <AppLink to="/usage">menu Pemakaian</AppLink>, mutasi saldo di{' '}
            <AppLink to="/transactions">menu Transaksi</AppLink>. Program Anda bisa membaca saldo lewat{' '}
            <code>GET /v1/balance</code>.
          </li>
        </ul>
      </DocSection>

      <DocSection id="anggaran" title="Anggaran">
        <p>
          Pasang anggaran harian dan bulanan per proyek atau per key, serta batas biaya per request di key. Request yang akan
          melewati salah satunya ditolak sebelum provider dipanggil, dengan kode <code>budget_exceeded</code> atau{' '}
          <code>max_cost_per_request_exceeded</code>.
        </p>
      </DocSection>

      <DocSection id="rate-limit" title="Rate limit">
        <p>Setiap API key punya batas laju sendiri. Ubah nilainya saat membuat atau mengedit key.</p>
        <DocTable
          className="docs-limits"
          head={['Batas', 'Bawaan', 'Catatan']}
          rows={[
            { key: 'rpm', cells: ['Request per menit', '60', 'Dihitung per menit kalender.'] },
            { key: 'tpm', cells: ['Token per menit', '150.000', 'Diperkirakan di awal, lalu dikoreksi dengan pemakaian nyata.'] },
            { key: 'conc', cells: ['Request bersamaan', '10', 'Termasuk streaming yang masih terbuka.'] },
          ]}
        />
        <p>
          Bila batas terlampaui, API membalas 429 (<code>rate_limit_exceeded</code> atau <code>concurrency_exceeded</code>) dengan
          header <code>Retry-After</code> dalam detik. SDK OpenAI otomatis mencoba ulang 429 beberapa kali.
        </p>
        <p>Baca hitungan saat ini untuk key yang dipakai:</p>
        <CodeBlock label="cURL" code={`curl ${BASE_URL}/limits \\\n  -H "Authorization: Bearer $${API_KEY_ENV}"`} />
      </DocSection>
    </>
  );
}

/* Kode error */

type Retry = 'ya' | 'nanti' | 'tidak';
interface ErrorRow {
  codes: string[];
  http: string;
  retry: Retry;
  text: ReactNode;
}

const ERROR_GROUPS: Array<{ id: string; title: string; rows: ErrorRow[] }> = [
  {
    id: 'key-akun',
    title: 'Key dan akun',
    rows: [
      {
        codes: ['invalid_api_key', 'api_key_revoked', 'api_key_expired'],
        http: '401',
        retry: 'tidak',
        text: (
          <>
            Key salah, sudah dicabut, atau kedaluwarsa. Periksa header <code>Authorization</code>, atau buat key baru.
          </>
        ),
      },
      {
        codes: ['organization_suspended', 'project_disabled'],
        http: '403',
        retry: 'tidak',
        text: 'Organisasi atau proyek sedang dinonaktifkan. Request tidak diproses.',
      },
      {
        codes: ['model_not_allowed'],
        http: '403',
        retry: 'tidak',
        text: (
          <>
            Key ini dibatasi ke model tertentu dan model yang diminta tidak termasuk. Pesannya menyebut model yang diizinkan. Pakai
            salah satunya, atau ubah akses model key. Lihat{' '}
            <DocLink slug="autentikasi" hash="batasi-model">
              Membatasi model per API key
            </DocLink>
            .
          </>
        ),
      },
    ],
  },
  {
    id: 'saldo-batas',
    title: 'Saldo dan batas',
    rows: [
      {
        codes: ['insufficient_balance'],
        http: '402',
        retry: 'nanti',
        text: (
          <>
            Saldo tersedia lebih kecil dari biaya maksimum request. Tidak ada biaya dan provider tidak dipanggil. Isi saldo, atau
            kecilkan <code>max_tokens</code>.
          </>
        ),
      },
      {
        codes: ['budget_exceeded'],
        http: '402',
        retry: 'tidak',
        text: 'Request akan melewati anggaran harian atau bulanan key atau proyek. Naikkan atau hapus anggarannya.',
      },
      {
        codes: ['max_cost_per_request_exceeded'],
        http: '400',
        retry: 'tidak',
        text: (
          <>
            Biaya maksimum melebihi batas per request di key. Kecilkan <code>max_tokens</code>.
          </>
        ),
      },
      {
        codes: ['rate_limit_exceeded', 'concurrency_exceeded'],
        http: '429',
        retry: 'ya',
        text: (
          <>
            Batas request per menit, token per menit, atau request bersamaan untuk key ini tercapai. Tunggu sesuai header{' '}
            <code>Retry-After</code>.
          </>
        ),
      },
    ],
  },
  {
    id: 'request-model',
    title: 'Request dan model',
    rows: [
      {
        codes: ['model_not_found'],
        http: '404',
        retry: 'tidak',
        text: (
          <>
            ID model tidak dikenal. Ambil ID yang benar dari <code>GET /v1/models</code>.
          </>
        ),
      },
      {
        codes: ['model_not_active'],
        http: '400, 409, 503',
        retry: 'tidak',
        text: 'Model sedang tidak aktif, atau tidak mendukung fitur yang diminta (streaming, tools, gambar). Pilih model lain.',
      },
      {
        codes: ['unsupported_parameter'],
        http: '400',
        retry: 'tidak',
        text: (
          <>
            Model tidak bisa memenuhi <code>tool_choice</code> yang memaksa atau <code>json_schema</code>.
          </>
        ),
      },
      {
        codes: ['context_length_error'],
        http: '400',
        retry: 'tidak',
        text: 'Prompt melebihi context window model. Ringkas prompt, atau pilih model berkonteks besar.',
      },
      { codes: ['content_filter_error'], http: '400', retry: 'tidak', text: 'Ditolak filter keamanan provider. Ubah isi prompt.' },
      {
        codes: ['no_eligible_provider'],
        http: '503',
        retry: 'ya',
        text: (
          <>
            Tidak ada model aktif yang memenuhi kebutuhan alias <code>auto:</code> untuk request ini.
          </>
        ),
      },
      {
        codes: ['upstream_model_unavailable'],
        http: '503',
        retry: 'tidak',
        text: 'Provider berhenti menyediakan model ini. Pilih model lain atau pasang model cadangan.',
      },
      {
        codes: ['pricing_unavailable'],
        http: '503',
        retry: 'tidak',
        text: 'Harga model belum terpasang. Kami menolak request daripada menagih dengan harga yang tidak jelas. Pilih model lain.',
      },
    ],
  },
  {
    id: 'provider',
    title: 'Gangguan provider',
    rows: [
      {
        codes: ['rate_limit_error'],
        http: '429',
        retry: 'ya',
        text: 'Provider sedang membatasi trafik. Coba lagi, atau pasang model cadangan.',
      },
      { codes: ['timeout_error'], http: '504', retry: 'ya', text: 'Provider tidak menjawab tepat waktu.' },
      {
        codes: ['upstream_error', 'overloaded_error'],
        http: '502, 503',
        retry: 'ya',
        text: (
          <>
            Provider error atau penuh. Aman diulang, dan <code>fallback_models</code> membantu di sini.
          </>
        ),
      },
      {
        codes: ['upstream_auth_error', 'upstream_quota_exceeded'],
        http: '502, 503',
        retry: 'nanti',
        text: 'Masalah kredensial atau kuota di sisi kami dengan provider, bukan pada key Anda.',
      },
    ],
  },
];

const RETRY: Record<Retry, { label: string; cls: string }> = {
  ya: { label: 'Aman diulang', cls: 'badge badge-accent' },
  nanti: { label: 'Ulang nanti', cls: 'badge badge-warn' },
  tidak: { label: 'Jangan diulang', cls: 'badge' },
};

export function ErrorsPage() {
  return (
    <>
      <PageHead slug="error">
        Error memakai bentuk OpenAI, jadi SDK Anda melempar exception yang tepat. Field <code>code</code> menjelaskan alasannya.
      </PageHead>

      <DocSection id="bentuk" title="Bentuk error">
        <CodeBlock
          label="402 · application/json"
          code={pretty({
            error: {
              message: 'Insufficient wallet balance for the maximum possible cost of this request',
              type: 'insufficient_quota',
              code: 'insufficient_balance',
              param: null,
              request_id: 'req_01M3265BF5DTVXT511Q8',
              details: { available_balance: 120.5, required: 12025, currency: 'IDR' },
            },
          })}
        />
        <p>
          Setiap error membawa <code>request_id</code>, yang juga dikirim di header <code>X-Request-Id</code>. Sertakan ID ini saat
          melapor masalah.
        </p>
      </DocSection>

      <DocSection id="daftar" title="Daftar kode">
        <p>
          Label <span className={RETRY.ya.cls}>{RETRY.ya.label}</span> berarti request yang sama boleh dikirim ulang. Error jenis
          ini juga memicu model cadangan bila proyek Anda memasangnya.
        </p>
        {ERROR_GROUPS.map((group) => (
          <div key={group.id} className="docs-err-group">
            <h3 id={group.id}>{group.title}</h3>
            <ul className="docs-errs">
              {group.rows.map((row) => (
                <li key={row.codes[0]} className="docs-err">
                  <div className="docs-err-head">
                    <span className="docs-err-codes">
                      {row.codes.map((code) => (
                        <code key={code}>{code}</code>
                      ))}
                    </span>
                    <span className="docs-err-meta">
                      <span className="docs-err-http">HTTP {row.http}</span>
                      <span className={RETRY[row.retry].cls}>{RETRY[row.retry].label}</span>
                    </span>
                  </div>
                  <p>{row.text}</p>
                </li>
              ))}
            </ul>
          </div>
        ))}
        <Callout>
          Masalah kredensial atau kuota di sisi kami selalu dibalas 502 atau 503, tidak pernah 401 untuk key Anda.
        </Callout>
      </DocSection>
    </>
  );
}

/* Integrasi */

const INTEGRATIONS = [
  { id: 'env', label: 'SDK OpenAI' },
  { id: 'anthropic', label: 'SDK Anthropic' },
  { id: 'langchain', label: 'LangChain' },
  { id: 'llamaindex', label: 'LlamaIndex' },
  { id: 'vercel', label: 'Vercel AI SDK' },
  { id: 'continue', label: 'Continue' },
  { id: 'aider', label: 'Aider' },
] as const;
type Integration = (typeof INTEGRATIONS)[number]['id'];

function integrationSnippet(id: Integration, model: string, embed: string): string {
  switch (id) {
    case 'env':
      return [
        '# SDK resmi OpenAI (Python dan Node.js) membaca dua variabel ini',
        `export OPENAI_BASE_URL="${BASE_URL}"`,
        `export OPENAI_API_KEY="$${API_KEY_ENV}"`,
        '',
        '# kode yang memanggil OpenAI() tanpa argumen langsung jalan',
        'python app.py',
      ].join('\n');
    case 'anthropic':
      return [
        '# SDK resmi Anthropic membaca dua variabel ini (tanpa /v1)',
        `export ANTHROPIC_BASE_URL="${API_ORIGIN}"`,
        `export ANTHROPIC_API_KEY="$${API_KEY_ENV}"`,
        '',
        '# kode yang memanggil Anthropic() tanpa argumen langsung jalan',
        'python app.py',
      ].join('\n');
    case 'langchain':
      return [
        '# pip install langchain-openai',
        'import os',
        'from langchain_openai import ChatOpenAI',
        '',
        'llm = ChatOpenAI(',
        `    model="${model}",`,
        `    base_url="${BASE_URL}",`,
        `    api_key=os.environ["${API_KEY_ENV}"],`,
        ')',
        'print(llm.invoke("Ringkas paragraf ini jadi dua kalimat: ...").content)',
      ].join('\n');
    case 'llamaindex':
      return [
        '# pip install llama-index-llms-openai-like',
        'import os',
        'from llama_index.llms.openai_like import OpenAILike',
        '',
        'llm = OpenAILike(',
        `    model="${model}",`,
        `    api_base="${BASE_URL}",`,
        `    api_key=os.environ["${API_KEY_ENV}"],`,
        '    is_chat_model=True,',
        ')',
        'print(llm.complete("Sebutkan tiga kota di Sulawesi."))',
      ].join('\n');
    case 'vercel':
      return [
        '// npm install ai @ai-sdk/openai-compatible',
        "import { createOpenAICompatible } from '@ai-sdk/openai-compatible';",
        "import { generateText } from 'ai';",
        '',
        'const partnerai = createOpenAICompatible({',
        `  name: '${BRAND_NAME.toLowerCase().replace(/\s+/g, '')}',`,
        `  baseURL: '${BASE_URL}',`,
        `  apiKey: process.env.${API_KEY_ENV},`,
        '});',
        '',
        `const { text } = await generateText({ model: partnerai('${model}'), prompt: 'Halo!' });`,
      ].join('\n');
    case 'continue':
      return [
        '# ~/.continue/config.yaml',
        'name: Konfigurasi saya',
        'version: 0.0.1',
        'schema: v1',
        'models:',
        `  - name: ${model} (${BRAND_NAME})`,
        '    provider: openai',
        `    model: ${model}`,
        `    apiBase: ${BASE_URL}`,
        '    apiKey: aia_live_xxxxxxxxxxxxxxxx',
        '    roles: [chat, edit, apply, autocomplete]',
        `  - name: ${embed} (${BRAND_NAME})`,
        '    provider: openai',
        `    model: ${embed}`,
        `    apiBase: ${BASE_URL}`,
        '    apiKey: aia_live_xxxxxxxxxxxxxxxx',
        '    roles: [embed]',
      ].join('\n');
    case 'aider':
      return [
        '# pip install aider-chat',
        `export OPENAI_API_BASE="${BASE_URL}"`,
        `export OPENAI_API_KEY="$${API_KEY_ENV}"`,
        '',
        `aider --model openai/${model}`,
      ].join('\n');
  }
}

const OTHER_TOOLS: Array<[string, ReactNode]> = [
  [
    'Cursor',
    'Settings → Models → isi OpenAI API Key dan aktifkan Override OpenAI Base URL. Hanya untuk chat; Tab dan fitur bawaan Cursor tetap memakai model Cursor.',
  ],
  ['Zed', 'Settings → Agent → tambahkan provider OpenAI Compatible dengan Base URL dan API key.'],
  ['Open WebUI', 'Admin Settings → Connections → OpenAI API.'],
  ['LibreChat', 'Tambahkan custom endpoint di librechat.yaml dengan baseURL dan apiKey.'],
  ['n8n', 'Kredensial OpenAI → isi Base URL dan API Key.'],
  ['OpenAI Agents SDK', <>Atur client OpenAI dengan base URL di atas; Responses API sudah didukung.</>],
];

const GUIDES = ['claude-code', 'codex', 'cline'] as const;

export function IntegrationsPage() {
  const { model, models } = useDocs();
  const [tool, setTool] = useState<Integration>('env');
  const embed = models?.find((m) => m.modality === 'embedding' && m.owned_by !== 'mock')?.id ?? 'text-embedding-3-small';

  return (
    <>
      <PageHead slug="integrasi">
        Partner AI berbicara dalam tiga format API populer. Hampir semua alat AI bisa langsung dipakai dengan satu API key dan saldo
        Rupiah yang sama.
      </PageHead>

      <DocSection id="format" title="Tiga format, satu API key">
        <DocTable
          head={['Format', 'Base URL', 'Dipakai oleh']}
          rows={[
            {
              key: 'openai',
              cells: [
                <span className="docs-cell-title">OpenAI Chat Completions</span>,
                <code>{BASE_URL}</code>,
                'Cline, Roo Code, Kilo Code, Continue, Cursor, Aider, LangChain, dan hampir semua SDK',
              ],
            },
            {
              key: 'anthropic',
              cells: [<span className="docs-cell-title">Anthropic Messages</span>, <code>{API_ORIGIN}</code>, 'Claude Code dan SDK Anthropic'],
            },
            {
              key: 'responses',
              cells: [<span className="docs-cell-title">OpenAI Responses</span>, <code>{BASE_URL}</code>, 'Codex dan OpenAI Agents SDK'],
            },
          ]}
        />
        <p>
          API key dari <AppLink to="/api-keys">menu API Key</AppLink> berlaku di ketiganya. Model apa pun di{' '}
          <DocLink slug="model" hash="katalog">daftar model</DocLink> bisa dipanggil lewat format mana pun, misalnya{' '}
          <code>{model}</code>.
        </p>
      </DocSection>

      <DocSection id="panduan" title="Panduan per alat">
        <ul className="docs-guides">
          {GUIDES.map((slug) => {
            const page = PAGES.find((p) => p.slug === slug)!;
            return (
              <li key={slug}>
                <Link to={docPath(slug)} className="docs-topic">
                  <span className="docs-topic-icon">
                    <Icon name={page.icon} size={17} />
                  </span>
                  <span className="docs-topic-text">
                    <span className="docs-topic-title">{page.title}</span>
                    <span className="docs-topic-sub">{page.blurb}</span>
                  </span>
                  <Icon name="arrow" size={15} className="docs-topic-go" />
                </Link>
              </li>
            );
          })}
        </ul>
      </DocSection>

      <DocSection id="contoh" title="Contoh konfigurasi">
        <CodeBlock code={integrationSnippet(tool, model, embed)} tabs={<Tabs tight tabs={INTEGRATIONS} value={tool} onChange={setTool} />} />
      </DocSection>

      <DocSection id="alat-lain" title="Alat lain">
        <DocTable
          className="docs-tools"
          head={['Alat', 'Tempat mengisi']}
          rows={OTHER_TOOLS.map(([name, where]) => ({ key: name, cells: [<span className="docs-cell-title">{name}</span>, where] }))}
        />
        <Callout title="Belum didukung" tone="warn">
          Audio (transkripsi dan suara), moderasi, Assistants, Batch, Files, dan fine-tuning belum tersedia. Endpoint yang ada: chat
          completions, <DocLink slug="anthropic">messages</DocLink>, <DocLink slug="responses">responses</DocLink>,{' '}
          <DocLink slug="embeddings">embeddings</DocLink>, <DocLink slug="completions">completions</DocLink>, gambar, video, dan models.
        </Callout>
      </DocSection>
    </>
  );
}

/* Pertanyaan umum */

/** Optional anchor id. */
const FAQ: Array<[string, ReactNode, string?]> = [
  [
    'Bagaimana cara membayar?',
    'Isi saldo Rupiah dari menu Saldo & Top Up, tanpa biaya tambahan. Isi Rp250.000 ke atas dapat bonus saldo. Setiap request memotong saldo sesuai pemakaiannya. Tidak ada langganan atau biaya bulanan.',
  ],
  [
    'Kalau kurs dolar naik, apakah saldo saya jadi lebih cepat habis?',
    'Tidak. Harga pemakaian untuk setiap saldo yang Anda isi dikunci saat pembelian, termasuk bonusnya, sampai saldo itu habis. Perubahan kurs setelahnya, naik maupun turun, hanya berlaku untuk isi saldo berikutnya. Karena itu biaya di log pemakaian bisa sedikit berbeda dari harga hari ini di daftar model.',
    'harga-terkunci',
  ],
  [
    'Apakah prompt dan jawaban kami disimpan?',
    'Tidak. Kami mencatat metadata request (model, jumlah token, biaya, latensi, status, waktu) untuk tagihan. Isi prompt hanya diteruskan ke provider model yang Anda pilih.',
  ],
  [
    'Kenapa sebagian saldo ditahan?',
    'Saat request berjalan, kami menahan biaya maksimumnya. Setelah selesai, Anda ditagih sesuai pemakaian nyata dan sisanya langsung kembali ke saldo tersedia.',
  ],
  [
    'Apa beda key aia_live_ dan aia_test_?',
    'Prefix mengikuti lingkungan proyek: aia_live_ untuk proyek PRODUCTION, aia_test_ untuk DEVELOPMENT. Keduanya memakai saldo yang sama dan memanggil model yang sama. Bedanya hanya supaya mudah dikenali.',
  ],
  [
    'Bisakah model yang kami minta diganti tanpa sepengetahuan kami?',
    'Tidak. Model hanya berpindah bila proyek Anda punya daftar model cadangan, dan field model di respons selalu menyebut model yang benar-benar menjawab.',
  ],
  [
    'Apa gunanya request_id?',
    'Setiap request punya ID unik, ada di body error dan header X-Request-Id. Sertakan ID itu saat melapor masalah supaya kami bisa melacak request yang tepat.',
  ],
];

export function FaqPage() {
  const { hash } = useLocation();
  return (
    <>
      <PageHead slug="faq">Jawaban singkat untuk pertanyaan yang sering muncul.</PageHead>
      <div className="docs-faq">
        {FAQ.map(([question, answer, id]) => (
          <details key={question} id={id} open={(id && hash === `#${id}`) || undefined}>
            <summary>
              <span>{question}</span>
              <Icon name="chevron" size={17} className="docs-faq-chevron" />
            </summary>
            <div className="docs-faq-body">
              <p>{answer}</p>
            </div>
          </details>
        ))}
      </div>
    </>
  );
}
